import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useTeam } from './TeamContext';
import {
  getTeamMembers,
  updateTeamMemberRole,
  removeTeamMember,
  regenerateInviteCode,
} from '../shared/storage/supabaseSync';

/**
 * Team admin dashboard — manage members, roles, and invite codes.
 * Scoped to the currently selected team.
 */
export default function AdminDashboard() {
  const { isLocalMode, user } = useAuth();
  const { currentTeam, isTeamAdmin, refreshTeams } = useTeam();
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [inviteCode, setInviteCode] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (isLocalMode || !isTeamAdmin || !currentTeam) {
      setLoading(false);
      return;
    }
    loadMembers();
    setInviteCode(currentTeam.invite_code || '');
  }, [isLocalMode, isTeamAdmin, currentTeam?.id]);

  const loadMembers = async () => {
    setLoading(true);
    const data = await getTeamMembers(currentTeam.id);
    setMembers(data);
    setLoading(false);
  };

  const handleRoleChange = async (memberId, currentRole) => {
    const newRole = currentRole === 'admin' ? 'member' : 'admin';
    if (memberId === user?.id && newRole === 'member') {
      if (!window.confirm('Remove your own admin access? You won\'t be able to undo this.')) return;
    }
    const { error } = await updateTeamMemberRole(currentTeam.id, memberId, newRole);
    if (error) {
      alert(error.message || 'Failed to update role');
      return;
    }
    setMembers(prev => prev.map(m =>
      m.id === memberId ? { ...m, role: newRole } : m
    ));
    refreshTeams();
  };

  const handleRemoveMember = async (memberId, memberName) => {
    if (!window.confirm(`Remove ${memberName} from ${currentTeam.name}?`)) return;
    const { error } = await removeTeamMember(currentTeam.id, memberId);
    if (error) {
      alert(error.message || 'Failed to remove member');
      return;
    }
    setMembers(prev => prev.filter(m => m.id !== memberId));
  };

  const handleRegenerateCode = async () => {
    if (!window.confirm('Regenerate invite code? The old code will stop working.')) return;
    const { data, error } = await regenerateInviteCode(currentTeam.id);
    if (error) {
      alert(error.message || 'Failed to regenerate code');
      return;
    }
    setInviteCode(data);
    refreshTeams();
  };

  const copyCode = () => {
    navigator.clipboard.writeText(inviteCode);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  if (isLocalMode) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h2 className="text-xl font-bold text-white mb-4">Admin Dashboard</h2>
        <p className="text-gray-400">Enable Supabase to manage team members.</p>
      </div>
    );
  }

  if (!currentTeam) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h2 className="text-xl font-bold text-white mb-4">Admin Dashboard</h2>
        <p className="text-gray-400 mb-4">Join or create a team first.</p>
        <Link to="/teams" className="text-blue-400 hover:text-blue-300 text-sm">Manage Teams</Link>
      </div>
    );
  }

  if (!isTeamAdmin) {
    return (
      <div className="max-w-lg mx-auto px-4 py-12 text-center">
        <h2 className="text-xl font-bold text-white mb-4">Access Denied</h2>
        <p className="text-gray-400">You need admin privileges for {currentTeam.name}.</p>
      </div>
    );
  }

  return (
    <div className="px-4 py-6 max-w-4xl mx-auto">
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold text-white mb-1">{currentTeam.name} — Admin</h1>
        <p className="text-gray-400 text-sm">Manage members and roles</p>
      </div>

      {/* Invite code section */}
      <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl p-4 mb-6">
        <h3 className="text-sm font-semibold text-white mb-2">Invite Code</h3>
        <p className="text-xs text-gray-500 mb-3">Share this code with people you want to join your team.</p>
        <div className="flex items-center gap-3">
          <code className="flex-1 px-3 py-2 rounded-lg bg-gray-700 text-gray-200 font-mono text-sm tracking-wider">
            {inviteCode}
          </code>
          <button
            onClick={copyCode}
            className="px-3 py-2 rounded-lg bg-blue-600 hover:bg-blue-500 text-white text-sm font-medium transition-colors"
          >
            {copied ? 'Copied!' : 'Copy'}
          </button>
          <button
            onClick={handleRegenerateCode}
            className="px-3 py-2 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 text-sm transition-colors"
          >
            Regenerate
          </button>
        </div>
      </div>

      {/* Team stats */}
      <div className="grid grid-cols-3 gap-4 mb-8">
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-white">{members.length}</div>
          <div className="text-xs text-gray-400 mt-1">Team Members</div>
        </div>
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-purple-400">
            {members.filter(m => m.role === 'admin').length}
          </div>
          <div className="text-xs text-gray-400 mt-1">Admins</div>
        </div>
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-lg p-4 text-center">
          <div className="text-2xl font-bold text-green-400">
            {members.filter(m => {
              const d = m.last_active_at;
              if (!d) return false;
              const diff = Date.now() - new Date(d).getTime();
              return diff < 7 * 24 * 60 * 60 * 1000;
            }).length}
          </div>
          <div className="text-xs text-gray-400 mt-1">Active This Week</div>
        </div>
      </div>

      {/* Member list */}
      {loading ? (
        <div className="text-center text-gray-500 py-8">Loading team...</div>
      ) : (
        <div className="bg-gray-800/50 border border-gray-700/50 rounded-xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-700">
                <th className="px-4 py-3 text-left text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-gray-400">Email</th>
                <th className="px-4 py-3 text-left text-gray-400">Role</th>
                <th className="px-4 py-3 text-left text-gray-400">Joined</th>
                <th className="px-4 py-3 text-left text-gray-400">Last Active</th>
                <th className="px-4 py-3 text-right text-gray-400">Actions</th>
              </tr>
            </thead>
            <tbody>
              {members.map(member => (
                <tr key={member.id} className="border-b border-gray-700/50">
                  <td className="px-4 py-3 text-white font-medium">
                    {member.display_name}
                    {member.id === user?.id && <span className="text-xs text-blue-500 ml-2">(you)</span>}
                  </td>
                  <td className="px-4 py-3 text-gray-400">{member.email}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                      member.role === 'admin' ? 'bg-purple-900/50 text-purple-300' : 'bg-gray-700 text-gray-300'
                    }`}>
                      {member.role}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {member.joined_at ? new Date(member.joined_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-gray-500 text-xs">
                    {member.last_active_at ? new Date(member.last_active_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-3 text-right space-x-2">
                    <button
                      onClick={() => handleRoleChange(member.id, member.role)}
                      className="text-xs text-blue-400 hover:text-blue-300"
                    >
                      {member.role === 'admin' ? 'Demote' : 'Promote'}
                    </button>
                    {member.id !== user?.id && (
                      <button
                        onClick={() => handleRemoveMember(member.id, member.display_name)}
                        className="text-xs text-red-400 hover:text-red-300"
                      >
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
